import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layers, BarChart3, LogOut, GraduationCap, Calendar, ClipboardList, BookOpen, Presentation, ChevronDown, IndianRupee, Receipt } from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import TrainerNotificationBell from './TrainerNotificationBell';
import api from '../lib/api';

const trainerNav = [
  { path: '/trainer/dashboard', label: 'My Batches', icon: Layers },
  { path: '/trainer/daily-attendance', label: 'Daily Attendance', icon: Calendar },
  { path: '/trainer/dpr-report', label: 'DPR Report', icon: ClipboardList },
  { path: '/trainer/report', label: 'My Report', icon: BarChart3 },
  { path: '/trainer/overdue', label: 'Overdue', icon: IndianRupee, badge: 'overdue' },
  { path: '/trainer/credit-notes', label: 'Credit Notes', icon: Receipt, badge: 'cn' },
  {
    label: 'Trainer Demo', icon: Presentation, groupKey: 'demo',
    children: [
      { path: '/trainer/demos', label: 'Demo Queue', end: true },
      { path: '/trainer/demos/new', label: 'Add New Demo' },
      { path: '/trainer/demos/report', label: 'Demo Report' },
      { path: '/trainer/demos/conversions', label: 'Conversions' },
    ],
  },
  { path: '/trainer/document-library', label: 'Document Library', icon: BookOpen },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${
    isActive ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
  }`;

function NavGroup({ item }) {
  const location = useLocation();
  const groupActive = item.children.some(c => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(groupActive);
  useEffect(() => { if (groupActive) setOpen(true); }, [groupActive]);
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="trainer-nav-group-trainer-demo"
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-150 ${groupActive ? 'text-violet-700 bg-violet-50' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}
      >
        <item.icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-slate-200 space-y-1">
          {item.children.map(c => (
            <NavLink
              key={c.path}
              to={c.path}
              end={c.end}
              data-testid={`trainer-nav-${c.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-sm text-[13px] font-medium transition-colors ${isActive ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`
              }
            >
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrainerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [overdueCount, setOverdueCount] = useState(0);
  useEffect(() => { api.get('/trainer/overdue/count').then(r => setOverdueCount(r.data.count || 0)).catch(() => {}); }, [location.pathname]);
  const [cnCount, setCnCount] = useState(0);
  useEffect(() => {
    const f = () => api.get('/trainer/cn-review/count').then(r => setCnCount(r.data.pending || 0)).catch(() => {});
    f();
    window.addEventListener('cn-count-changed', f);
    return () => window.removeEventListener('cn-count-changed', f);
  }, [location.pathname]);
  const initials = (user?.full_name || user?.name || '??').split(' ').map(n => n[0]).join('').toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/trainer/login');
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="trainer-layout">
      <aside className="w-56 flex-shrink-0 border-r border-border bg-white flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-violet-600 flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-heading font-bold text-sm tracking-tight block">Trainer Portal</span>
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {trainerNav.map(item => (
              item.children ? <NavGroup key={item.label} item={item} /> : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                data-testid={`trainer-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={linkCls}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {item.badge === 'cn' && cnCount > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="trainer-nav-cn-count">{cnCount}</span>
                )}
                {item.badge === 'overdue' && overdueCount > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="trainer-nav-overdue-count">{overdueCount}</span>
                )}
              </NavLink>
              )
            ))}
          </nav>
        </ScrollArea>

        <div className="border-t border-border p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full text-left hover:bg-secondary rounded-sm p-2 transition-colors duration-150" data-testid="trainer-user-menu">
                <Avatar className="w-8 h-8 rounded-sm">
                  <AvatarFallback className="rounded-sm bg-violet-600 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.full_name || user?.name}</p>
                  <p className="text-[10px] text-muted-foreground">Trainer</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-muted-foreground" disabled>{user?.username || ''}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="trainer-logout-btn" onClick={handleLogout} className="text-red-600">
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 glass-header border-b border-border flex items-center justify-between px-8 sticky top-0 z-40">
          <div />
          <div className="flex items-center gap-3">
            <TrainerNotificationBell />
            <span className="text-xs font-data text-muted-foreground">{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </header>
        <ScrollArea className="flex-1">
          <main className="p-6 md:p-8 max-w-[1400px]">
            <Outlet />
          </main>
        </ScrollArea>
      </div>
    </div>
  );
}
